import React, { useState, useEffect } from 'react';
import { listSentEmails } from '../utils/apiClient';
import styled from 'styled-components';
import { Line, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import Header from '../components/Header';

// Styled components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const StatValue = styled.p`
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
`;

const StatLabel = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.5rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ChartContainer = styled.div`
  height: 300px;
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const TableHeader = styled.th`
  padding: 0.75rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  color: #6b7280;
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  
  background-color: ${props => props.isActive ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.isActive ? '#065f46' : '#b91c1c'};
`;

const LoadingIndicator = styled.div`
  height: 150px;
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoDataMessage = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`;

const LinkText = styled.a`
  color: #3b82f6;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  display: block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmailStats = () => {
  const [loading, setLoading] = useState(true);
  const [globalStats, setGlobalStats] = useState({
    total_sent: 0,
    total_opened: 0,
    emails_with_clicks: 0,
    total_clicks: 0
  });
  const [timeStats, setTimeStats] = useState([]);
  const [rateTimeStats, setRateTimeStats] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [topRecipients, setTopRecipients] = useState([]);
  const [allEmails, setAllEmails] = useState([]);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      try {
        // 1. Récupérer tous les emails envoyés via l'API
        const emailsData = await listSentEmails();
        setAllEmails(emailsData || []);
        
        // Calculer les statistiques globales manuellement
        const total_sent = emailsData ? emailsData.length : 0;
        const total_opened = emailsData ? emailsData.filter(item => item.opened).length : 0;
        const emails_with_clicks = emailsData ? emailsData.filter(item => item.total_clicks > 0).length : 0;
        const total_clicks = emailsData ? emailsData.reduce((sum, item) => sum + (item.total_clicks || 0), 0) : 0;
        
        setGlobalStats({ total_sent, total_opened, emails_with_clicks, total_clicks });
        
        // 2. Récupérer et préparer les données temporelles
        // Grouper les données par jour
        const timeStatsByDay = {};
        const rateStatsByDay = {};
        
        if (emailsData && emailsData.length > 0) {
          emailsData.forEach(email => {
            const date = new Date(email.sent_at);
            const day = date.toISOString().split('T')[0]; // Format YYYY-MM-DD
            
            // Initialiser le jour s'il n'existe pas
            if (!timeStatsByDay[day]) {
              timeStatsByDay[day] = {
                day: day,
                emails_sent: 0,
                emails_opened: 0,
                total_clicks: 0
              };
            }
            
            if (!rateStatsByDay[day]) {
              rateStatsByDay[day] = {
                day: day,
                emails_sent: 0,
                emails_opened: 0,
                emails_clicked: 0
              };
            }
            
            // Incrémenter les compteurs
            timeStatsByDay[day].emails_sent += 1;
            if (email.opened) {
              timeStatsByDay[day].emails_opened += 1;
              rateStatsByDay[day].emails_opened += 1;
            }
            if (email.total_clicks > 0) {
              timeStatsByDay[day].total_clicks += email.total_clicks;
              rateStatsByDay[day].emails_clicked += 1;
            }
            rateStatsByDay[day].emails_sent += 1;
          });
        }
        
        // Convertir les objets en tableaux pour les graphiques
        const timeStatsArray = Object.values(timeStatsByDay).sort((a, b) => a.day.localeCompare(b.day));
        setTimeStats(timeStatsArray);
        
        // Calculer les taux pour chaque jour
        const rateStatsArray = Object.values(rateStatsByDay).map(day => ({
          day: day.day,
          open_rate: day.emails_sent > 0 ? (day.emails_opened / day.emails_sent) * 100 : 0,
          click_rate: day.emails_sent > 0 ? (day.emails_clicked / day.emails_sent) * 100 : 0
        })).sort((a, b) => a.day.localeCompare(b.day));
        
        setRateTimeStats(rateStatsArray);
        
        // 3. Récupérer les 10 derniers emails envoyés
        setRecentEmails(emailsData ? emailsData.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at)).slice(0, 10) : []);
        
        // 4. Analyser les données pour trouver les destinataires les plus actifs
        const recipientStats = {};

        if (emailsData && emailsData.length > 0) {
          emailsData.forEach(email => {
            const recipient = email.recipient_email;
            
            // Initialiser les statistiques pour ce destinataire s'il n'existe pas encore
            if (!recipientStats[recipient]) {
              recipientStats[recipient] = {
                recipient: recipient,
                emails_received: 0,
                emails_opened: 0,
                emails_clicked: 0
              };
            }
            
            // Mettre à jour les statistiques
            recipientStats[recipient].emails_received += 1;
            if (email.opened) {
              recipientStats[recipient].emails_opened += 1;
            }
            if (email.total_clicks > 0) {
              recipientStats[recipient].emails_clicked += 1;
            }
          });
        }

        // Calculer les taux et convertir en tableau
        const topRecipientsArray = Object.values(recipientStats)
          .map(stats => ({
            recipient: stats.recipient,
            emails_received: stats.emails_received,
            open_rate: stats.emails_received > 0 ? (stats.emails_opened / stats.emails_received * 100).toFixed(2) : 0,
            click_rate: stats.emails_received > 0 ? (stats.emails_clicked / stats.emails_received * 100).toFixed(2) : 0
          }))
          .sort((a, b) => parseFloat(b.open_rate) - parseFloat(a.open_rate)) // Trier par taux d'ouverture décroissant
          .slice(0, 20); // Garder seulement les 20 premiers

        setTopRecipients(topRecipientsArray);
        
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchStats();
  }, []);
  
  // Calculer les taux à partir des données globales
  const openRate = globalStats.total_sent > 0 
    ? (globalStats.total_opened / globalStats.total_sent * 100).toFixed(2) 
    : 0;
    
  const clickRate = globalStats.total_sent > 0 
    ? (globalStats.emails_with_clicks / globalStats.total_sent * 100).toFixed(2) 
    : 0;
  
  // Préparer les données pour le graphique d'évolution temporelle (nombres absolus)
  const timeChartData = {
    labels: timeStats.map(item => new Date(item.day).toLocaleDateString('fr-CA')),
    datasets: [
      {
        label: 'Emails Envoyés',
        data: timeStats.map(item => item.emails_sent),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true
      },
      {
        label: 'Emails Ouverts',
        data: timeStats.map(item => item.emails_opened),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true
      },
      {
        label: 'Clics Totaux',
        data: timeStats.map(item => item.total_clicks),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderWidth: 2,
        fill: true
      }
    ]
  };
  
  // Options pour le graphique d'évolution temporelle
  const timeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Nombre'
        }
      }
    }
  };
  
  // Préparer les données pour le graphique des taux
  const rateChartData = {
    labels: rateTimeStats.map(item => new Date(item.day).toLocaleDateString('fr-CA')),
    datasets: [
      {
        label: 'Taux d\'Ouverture (%)',
        data: rateTimeStats.map(item => item.open_rate.toFixed(2)),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true
      },
      {
        label: 'Taux de Clic (%)',
        data: rateTimeStats.map(item => item.click_rate.toFixed(2)),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderWidth: 2,
        fill: true
      }
    ]
  };
  
  // Options pour le graphique des taux
  const rateChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Pourcentage (%)'
        }
      }
    }
  };

  return (
    <>
      <Header />
      <Container>
        <Title>Statistiques des Emails</Title>
        
        {loading ? (
          // Indicateur de chargement
          <StatsGrid>
            <LoadingIndicator>Chargement...</LoadingIndicator>
            <LoadingIndicator>Chargement...</LoadingIndicator>
            <LoadingIndicator>Chargement...</LoadingIndicator>
          </StatsGrid>
        ) : (
          <>
            {/* Statistiques globales */}
            <StatsGrid>
              <Card>
                <CardHeader>
                  <CardTitle>Emails Envoyés</CardTitle>
                </CardHeader>
                <CardContent>
                  <StatValue style={{ color: '#3b82f6' }}>{globalStats.total_sent}</StatValue>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Taux d'Ouverture</CardTitle>
                </CardHeader>
                <CardContent>
                  <StatValue style={{ color: '#10b981' }}>{openRate}%</StatValue>
                  <StatLabel>{globalStats.total_opened} emails ouverts</StatLabel>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Taux de Clic</CardTitle>
                </CardHeader>
                <CardContent>
                  <StatValue style={{ color: '#8b5cf6' }}>{clickRate}%</StatValue>
                  <StatLabel>{globalStats.emails_with_clicks} emails avec clics</StatLabel>
                </CardContent>
              </Card>
            </StatsGrid>
            
            {/* Graphiques */}
            <ChartsGrid>
              <Card>
                <CardHeader>
                  <CardTitle>Évolution Temporelle</CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer>
                    {timeStats.length > 0 ? (
                      <Line 
                        data={timeChartData} 
                        options={timeChartOptions} 
                      />
                    ) : (
                      <NoDataMessage>
                        <p>Aucune donnée disponible</p>
                      </NoDataMessage>
                    )}
                  </ChartContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Évolution des Taux</CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer>
                    {rateTimeStats.length > 0 ? (
                      <Line 
                        data={rateChartData} 
                        options={rateChartOptions} 
                      />
                    ) : (
                      <NoDataMessage>
                        <p>Aucune donnée disponible</p>
                      </NoDataMessage>
                    )}
                  </ChartContainer>
                </CardContent>
              </Card>
            </ChartsGrid>

            {/* Destinataires les plus actifs */}
            <Card>
              <CardHeader>
                <CardTitle>Destinataires les Plus Actifs</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Destinataire</TableHeader>
                        <TableHeader>Emails Reçus</TableHeader>
                        <TableHeader>Taux d'Ouverture</TableHeader>
                        <TableHeader>Taux de Clic</TableHeader>
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {topRecipients.length > 0 ? topRecipients.map((recipient, index) => (
                        <TableRow key={index}>
                          <TableCell>{recipient.recipient}</TableCell>
                          <TableCell>{recipient.emails_received}</TableCell>
                          <TableCell>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: parseFloat(recipient.open_rate) > 50 ? '#10b981' : 
                                    parseFloat(recipient.open_rate) > 25 ? '#f59e0b' : '#ef4444'
                            }}>
                              {recipient.open_rate}%
                            </div>
                          </TableCell>
                          <TableCell>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: parseFloat(recipient.click_rate) > 50 ? '#10b981' : 
                                    parseFloat(recipient.click_rate) > 25 ? '#f59e0b' : '#ef4444'
                            }}>
                              {recipient.click_rate}%
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan="4" style={{ textAlign: 'center' }}>
                            Aucune donnée disponible
                          </TableCell>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Tableau des derniers emails */}
            <Card>
              <CardHeader>
                <CardTitle>Derniers Emails Envoyés</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Destinataire</TableHeader>
                        <TableHeader>Date d'Envoi</TableHeader>
                        <TableHeader>Ouvert</TableHeader>
                        <TableHeader>Ouvertures</TableHeader>
                        <TableHeader>Clics</TableHeader>
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {recentEmails.length > 0 ? recentEmails.map((email) => (
                        <TableRow key={email.id}>
                          <TableCell>{email.recipient_email}</TableCell>
                          <TableCell>{new Date(email.sent_at).toLocaleString('fr-CA')}</TableCell>
                          <TableCell>
                            <StatusBadge isActive={email.opened}>
                              {email.opened ? 'Oui' : 'Non'}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{email.total_opens}</TableCell>
                          <TableCell>{email.total_clicks}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan="5" style={{ textAlign: 'center' }}>
                            Aucun email récent
                          </TableCell>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardContent>
            </Card>

          </>
        )}
      </Container>
    </>
  );
};

export default EmailStats;